import { FormGroup, Validators } from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { invalidAddressString } from './invalid-strings.utils';

type RemainingDataForm = {
  formControlName: string;
  placeholder: string;
  type: 'text' | 'date' | 'tel' | 'email' | 'checkbox' | 'radio';
  defaultErrorMessage: string;
};

export const payerFields: string[] = [
  'payerFirstName',
  'payerSecondName',
  'payerFirstLastName',
  'payerSecondLastName',
  'payerIdentificationType',
  'payerDocument',
  'payerExpeditionDate',
  'payerBornDate',
  'payerCountry',
  'payerCity',
  'payerAddress',
  'payerNumber',
  'payerEmail',
];

const remainingDataForm: RemainingDataForm[] = [
  {
    formControlName: 'bornDate',
    placeholder: 'Fecha de nacimiento',
    type: 'date',
    defaultErrorMessage: 'Asegúrate de ingresar una fecha válida.',
  },
  {
    formControlName: 'expeditionDate',
    placeholder: 'Fecha de expedición',
    type: 'date',
    defaultErrorMessage: 'Asegúrate de ingresar una fecha válida.',
  },
  {
    formControlName: 'country',
    placeholder: 'País',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de llenar este campo.',
  },
  {
    formControlName: 'city',
    placeholder: 'Ciudad',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de llenar este campo.',
  },
  {
    formControlName: 'address',
    placeholder: 'Dirección',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar una dirección válida.',
  },
  {
    formControlName: 'number',
    placeholder: 'Teléfono',
    type: 'text',
    defaultErrorMessage:
      'Asegúrate de ingresar un número válido de 10 dígitos.',
  },
  {
    formControlName: 'email',
    placeholder: 'Correo electrónico',
    type: 'email',
    defaultErrorMessage: 'Asegúrate de ingresar un correo válido.',
  },
  {
    formControlName: 'termsAndConditions',
    placeholder: 'Términos y condiciones',
    type: 'checkbox',
    defaultErrorMessage: 'Debe aceptar los términos y condiciones.',
  },
  {
    formControlName: 'isPayer',
    placeholder: '¿Es pagador?',
    type: 'radio',
    defaultErrorMessage: 'Debe seleccionar si es pagador o no.',
  },
  {
    formControlName: 'payerFirstName',
    placeholder: 'Primer Nombre del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar un nombre válido.',
  },
  {
    formControlName: 'payerSecondName',
    placeholder: 'Segundo Nombre del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar un nombre válido.',
  },
  {
    formControlName: 'payerFirstLastName',
    placeholder: 'Primer Apellido del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar un apellido válido.',
  },
  {
    formControlName: 'payerSecondLastName',
    placeholder: 'Segundo Apellido del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar un apellido válido.',
  },
  {
    formControlName: 'payerIdentificationType',
    placeholder: 'Tipo de Documento del Pagador',
    type: 'text',
    defaultErrorMessage: 'El tipo de documento es obligatorio.',
  },
  {
    formControlName: 'payerDocument',
    placeholder: 'Documento del Pagador',
    type: 'text',
    defaultErrorMessage: 'Parece que dejaste un campo importante vació.',
  },
  {
    formControlName: 'payerExpeditionDate',
    placeholder: 'Fecha de Expedición del Documento del Pagador',
    type: 'date',
    defaultErrorMessage: 'Asegúrate de ingresar una fecha válida.',
  },
  {
    formControlName: 'payerBornDate',
    placeholder: 'Fecha de Nacimiento del Pagador',
    type: 'date',
    defaultErrorMessage: 'Asegúrate de ingresar una fecha válida.',
  },
  {
    formControlName: 'payerCountry',
    placeholder: 'País del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de llenar este campo.',
  },
  {
    formControlName: 'payerCity',
    placeholder: 'Ciudad del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de llenar este campo.',
  },
  {
    formControlName: 'payerAddress',
    placeholder: 'Dirección del Pagador',
    type: 'text',
    defaultErrorMessage: 'Asegúrate de ingresar una dirección válida.',
  },
  {
    formControlName: 'payerNumber',
    placeholder: 'Teléfono del Pagador',
    type: 'text',
    defaultErrorMessage:
      'Asegúrate de ingresar un número válido de 10 dígitos.',
  },
  {
    formControlName: 'payerEmail',
    placeholder: 'Correo Electrónico del Pagador',
    type: 'email',
    defaultErrorMessage: 'Asegúrate de ingresar un correo válido.',
  },
];

export class RemainingDataFormUtils {
  static getFormFields(): RemainingDataForm[] {
    return remainingDataForm;
  }

  static getFieldByName(name: string): RemainingDataForm | undefined {
    return remainingDataForm.find((field) => field.formControlName === name);
  }

  static getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    const formField = this.getFormFields().find(
      (field) => field.formControlName === fieldName
    );

    if (!formField) {
      return 'Campo no válido.';
    }

    const control = formGroup.get(fieldName);

    if (fieldName === 'payerDocument' && control && control.errors) {
      // Obtiene la lista de claves del objeto de errores (ej: ['onlyNumbers', 'length']).
      const errorKeys = Object.keys(control.errors);
      // Si hay al menos una clave de error...
      if (errorKeys.length > 0) {
        // Toma la primera clave de error.
        const firstErrorKey = errorKeys[0];
        // Devuelve el mensaje asociado a esa clave.

        return firstErrorKey === 'required'
          ? formField.defaultErrorMessage
          : control.errors[firstErrorKey];
      }
    }
    if (control && control.errors) {
      return formField.defaultErrorMessage;
      // Puedes agregar más validaciones aquí según sea necesario.
    }
    return '';
  }
}

/**
 * Valida que una fecha no sea una de las restringidas proporcionadas.
 * @param restrictedDates Array de fechas restringidas (en formato `YYYY-MM-DD`)
 */
export function restrictedDatesValidator(): ValidatorFn {
  // Fechas restringidas
  const restrictedDates = [
    '1801-01-01',
    '1800-01-01',
    '1901-01-01',
    '1900-01-01',
  ];
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null; // Si no hay valor, no es inválido
    const value = control.value;
    return restrictedDates.includes(value) ? { restrictedDate: true } : null;
  };
}

/**
 * Valida que una fecha sea menor o igual a la fecha actual.
 */
export function maxCurrentDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const inputDate = new Date(control.value);
    const today = new Date();
    return inputDate > today ? { maxCurrentDate: true } : null;
  };
}

/**
 * Valida que una fecha de expedición sea al menos 18 años posterior a la fecha de nacimiento.
 * @param birthDateControlName El nombre del control que contiene la fecha de nacimiento
 */
export function minYearsFromBirthValidator(
  birthDateControlName: string
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !control.parent) return null;

    const birthDate = control.parent.get(birthDateControlName)?.value;
    if (!birthDate) {
      return null;
    }
    const inputDate = new Date(control.value);
    const minExpeditionDate = new Date(birthDate);

    minExpeditionDate.setFullYear(minExpeditionDate.getFullYear() + 18); // Fecha de nacimiento + 18 años

    return inputDate >= minExpeditionDate ? null : { minYearsFromBirth: true };
  };
}

/**
 * Método para manejar dinámicamente las validaciones de los campos del payer
 * @param formGroup - Formulario base
 * @param isRequired - True si los campos deben ser obligatorios, False si no
 */
export function togglePayerFieldsValidation(
  formGroup: FormGroup,
  isRequired: boolean,
  payerFieldsOriginalValidators: { [key: string]: ValidatorFn | ValidatorFn[] }
): void {
  // Itera sobre los campos definidos en el objeto de validadores originales

  Object.keys(payerFieldsOriginalValidators).forEach((field) => {
    const control = formGroup.get(field);

    if (control) {
      if (isRequired) {
        // Restablece los validadores originales del campo
        control.setValidators(payerFieldsOriginalValidators[field]);
        if (field !== 'payerSecondName' && field !== 'payerSecondLastName') {
          control.addValidators([Validators.required]);
        }
      } else {
        // Limpia todos los validadores del campo
        control.clearValidators();
      }
      // Actualiza el estado y la validez del control
      control.updateValueAndValidity();
    }
  });
}

/**
 * Validación de caracteres permitidos en una dirección.
 * Solo permite:
 * - Letras (incluyendo Ñ)
 * - Números (0-9)
 * - Espacios en blanco
 * - Los caracteres especiales: . - # * / \
 */
export function allowedCharactersValidator(): ValidatorFn {
  // Se reemplaza \s por un espacio literal ' '.
  // Se mueve el guion '-' al final para no tener que escaparlo.
  // Se quitan escapes innecesarios de '.' y '*' ya que no son especiales dentro de [].
  const allowedRegex = /^[A-Za-z0-9Ññ .#*\/\\-]+$/;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      // Si el control está vacío, se considera válido.
      // La validación de campo requerido se maneja con Validators.required.
      return null;
    }

    const isValid = allowedRegex.test(control.value);

    return isValid
      ? null // Si la prueba es exitosa, el valor es válido.
      : {
          invalidCharacters: 'La dirección contiene caracteres no permitidos.',
        };
  };
}

/**
 * Validación de frases restringidas.
 * Verifica que el valor del campo no coincida con ninguna frase dentro de una lista predefinida.
 * Insensible a mayúsculas y minúsculas.
 */
export function restrictedPhrasesValidator(): ValidatorFn {
  const normalizedPhrases = invalidAddressString.map((phrase) =>
    phrase.toLowerCase().trim()
  );
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Si el control está vacío, no hay error (el "required" debe manejarse aparte)
    }
    const value = control.value.toLowerCase().trim();
    return normalizedPhrases.includes(value)
      ? { restrictedPhrase: 'El valor ingresado no es válido.' }
      : null;
  };
}

/**
 * Función para normalizar texto:
 * - Elimina espacios redundantes
 * - Convierte a mayúsculas
 * - Elimina tildes
 */
export function normalizeText(inputValue: string): string {
  return inputValue
    .replace(/\s\s+/g, ' ') // Reemplazar espacios redundantes
    .trim() // Eliminar espacios al inicio y final
    .toUpperCase() // Convertir a mayúsculas
    .normalize('NFD') // Normalizar Unicode
    .replace(/[\u0300-\u036f]/g, ''); // Eliminar tildes
}

/**
 * Validador que verifica que un nombre o apellido sea alfabético,
 * permitiendo únicamente letras y el punto (.).
 */
export function alphabeticValidator(): ValidatorFn {
  const regex = /^[A-Z. ]+$/; // Permitir solo letras, punto y espacios
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || regex.test(control.value.toUpperCase())) {
      return null; // Es válido si no hay valor o cumple la regex
    }
    return {
      invalidAlphabetic: 'El valor contiene caracteres no permitidos.',
    };
  };
}

/**
 * Validador para verificar la longitud mínima y máxima.
 * @param min Longitud mínima del campo
 * @param max Longitud máxima del campo
 */
export function lengthValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isPayer = control.parent?.get('isPayer')?.value;
    if (isPayer !== 'N') return null;
    const value = control.value || '';
    if (value.length >= min && value.length <= max) {
      return null; // Es válido
    }
    return {
      invalidLength: `El valor debe tener entre ${min} y ${max} caracteres.`,
    };
  };
}

/**
 * Valida que un apellido:
 * 1. Contenga únicamente caracteres alfabéticos (A-Z) y puntos.
 * 2. Si es compuesto, tenga una estructura válida (un único apellido
 * principal y el resto deben ser conectores permitidos).
 * Ejemplos válidos: "GARCIA", "DEL CAMPO", "DE.LA.CRUZ".
 * Ejemplos inválidos: "PEREZ GOMEZ", "DE LA", "GARCIA-123".
 */
export function compositeLastNameValidator(): ValidatorFn {
  // Lista de conectores permitidos, todos en mayúsculas para una comparación consistente.
  const allowedConnectors = [
    'DE',
    'DEL',
    'SAN',
    'LA',
    'VIUDA',
    'VDA',
    'LOS',
    'LAS',
    'SANTO',
  ];
  // Regex para validar que cada palabra solo contenga letras y puntos.
  const wordRegex = /^[A-Z.]+$/;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || typeof control.value !== 'string') {
      return null; // No validar si no hay valor o no es un string.
    }

    // Normalizar el valor: quitar espacios extra y convertir a mayúsculas.
    const value = control.value.trim().toUpperCase();

    if (!value) {
      return null; // Si después de limpiar no queda nada, es válido para este validador.
    }

    const words = value.split(/\s+/); // Dividir el apellido en palabras.

    // --- LÓGICA DE 'alphabeticValidator' INTEGRADA ---
    // Regla: El contenido debe ser alfabético, permitiendo letras y punto.
    for (const word of words) {
      if (!wordRegex.test(word)) {
        return {
          invalidAlphabetic: 'El apellido solo puede contener letras y puntos.',
        };
      }
    }
    // --- FIN DE LA INTEGRACIÓN ---

    // Si solo hay una palabra, ya validamos sus caracteres, entonces es válido.
    if (words.length === 1) {
      return null;
    }

    // Contar cuántas palabras NO son conectores. Esas son los "apellidos principales".
    const mainLastNameParts = words.filter(
      (word) => !allowedConnectors.includes(word)
    );

    // La regla de oro: un apellido compuesto válido debe tener EXACTAMENTE un apellido principal.
    if (mainLastNameParts.length === 1) {
      return null; // Es válido. Ej: "DEL" (conector) "CAMPO" (principal).
    } else {
      // Si hay más de un principal ("PEREZ GOMEZ") o ninguno ("DE LA"), es inválido.
      return {
        invalidCompositeName:
          'El apellido compuesto es inválido. Debe contener un solo apellido principal.',
      };
    }
  };
}

/**
 * Validador para restringir strings no válidos definidos en una lista.
 * @param restrictedStrings Lista de strings no permitidos
 */
export function restrictedStringsValidator(
  restrictedStrings: string[]
): ValidatorFn {
  const normalizedRestrictedStrings = restrictedStrings.map((str) =>
    str.toUpperCase().trim()
  );
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Si no hay valor, no validamos nada
    }
    const normalizedValue = control.value.toUpperCase().trim();
    return normalizedRestrictedStrings.includes(normalizedValue)
      ? {
          restrictedString: 'El valor ingresado no es válido.',
        }
      : null;
  };
}

/**
 * Validador para el número de documento según el tipo seleccionado.
 * @param tipoControlName El nombre del control que contiene el tipo de documento
 */
export function documentNumberValidator(tipoControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Si el control o su 'padre' (el form group) no existen, no validar.
    if (!control.parent) {
      return null;
    }

    // Obtener el valor del campo y limpiarlo de espacios.
    const numeroDocumento = control.value ? String(control.value).trim() : '';

    // Si el campo está vacío después de limpiarlo, es válido para este validador.
    // La obligatoriedad la define `Validators.required`.
    if (!numeroDocumento) {
      return null;
    }

    const tipoDocumento = control.parent.get(tipoControlName)?.value;

    switch (tipoDocumento) {
      // REGLA: Cédula de Ciudadanía
      case 'C': {
        // Usamos llaves para crear un nuevo ámbito para las variables

        // Regex para detectar si hay alguna letra (incluyendo 'ñ')
        const hasLetters = /[a-zA-ZñÑ]/.test(numeroDocumento);
        // Regex para detectar si hay cualquier caracter que no sea un número
        const hasNonNumeric = /[^\d]/.test(numeroDocumento);

        // 1. Primera y más específica validación: ¿Contiene letras?
        if (hasLetters) {
          return {
            onlyNumbers: 'Este campo debe contener únicamente números.', // Nuevo error específico
          };
        }

        // 2. Si no tiene letras, ¿contiene otros caracteres no numéricos?
        if (hasNonNumeric) {
          return {
            specialChars: 'Evita usar caracteres especiales como @,#,$,ñ.', // Nuevo error específico
          };
        }

        // 3. Validación de longitud (se mantiene igual)
        if (numeroDocumento.length < 3 || numeroDocumento.length > 10) {
          return { length: 'El campo debe tener entre 3 y 10 dígitos.' };
        }

        break;
      }
      // REGLA: Pasaporte y Cédula de Extranjería (comparten las mismas reglas)
      case 'P':
      case 'E':
        if (!/^[a-zA-Z0-9]+$/.test(numeroDocumento)) {
          return {
            characters: 'Evita usar caracteres especiales como @,#,$,ñ',
          };
        }
        if (numeroDocumento.length < 1 || numeroDocumento.length > 11) {
          return { length: 'El campo debe tener entre 1 y 11 caracteres.' };
        }
        break;

      // Si el tipo de documento no es ninguno de los anteriores, no se aplica validación de formato.
      default:
        return null;
    }

    // Si pasa todas las validaciones del 'case' correspondiente, es válido.
    return null;
  };
}
